import React, { useEffect, useMemo } from "react";
import { Card, Row, Col, Input, Radio } from "antd";
import NumberFormat from "react-number-format";
import RadioButton from "../../../../components/utils/radioButton";

const ArrobaProductionCard = ({
  herds = [],
  values,
  setFieldValue,
  errors,
  submitCount,
  translation,
}) => {
  const reportType = values.arrobaProductionContract?.reportType || null;
  const uniqueData = values.arrobaProductionContract?.uniqueData || {
    carcassHarnessingPercentage: null,
  };
  const valuePerArroba =
    values.arrobaProductionContract?.valuePerArroba || null;

  useEffect(() => {
    // Sincroniza dados de herdsWithData para herds na inicialização (quando carrega dados existentes)
    if (
      values.arrobaProductionContract?.herdsWithData &&
      reportType === "cjto" &&
      values.herds?.length > 0
    ) {
      const hasDataInHerds = values.herds.some(
        (h) => h.carcassHarnessingPercentage !== null,
      );

      // Se herds não tem os dados mas herdsWithData tem, copia para herds
      if (!hasDataInHerds) {
        const updatedHerds = (values.herds || []).map((herd) => {
          const herdData = values.arrobaProductionContract.herdsWithData.find(
            (h) => h.id === herd.id,
          );
          return {
            ...herd,
            carcassHarnessingPercentage:
              herdData?.carcassHarnessingPercentage ?? null,
          };
        });
        setFieldValue("herds", updatedHerds);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Sincroniza os dados dos herds quando a lista de herds (prop) muda
    // Garante que novos herds tenham os campos necessários
    const herdsIds = (herds || [])
      .map((h) => h.id)
      .sort()
      .join(",");
    const valuesHerdsIds = (values.herds || [])
      .map((h) => h.id)
      .sort()
      .join(",");

    // Só atualiza se a lista de IDs mudou (herds adicionados ou removidos)
    if (herdsIds !== valuesHerdsIds && herdsIds) {
      const updatedHerds = (herds || []).map((herd) => {
        const existingHerd = values.herds?.find((h) => h.id === herd.id);
        // Preserva os dados existentes ou inicializa como null
        return {
          ...herd,
          carcassHarnessingPercentage:
            existingHerd?.carcassHarnessingPercentage ?? null,
        };
      });
      setFieldValue("herds", updatedHerds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [herds]);

  const updateArrobaProductionContract = (updates) => {
    const currentContract = values.arrobaProductionContract || {};
    const newReportType = updates.reportType ?? reportType;

    // Gera herdsWithData a partir de values.herds quando reportType é "cjto"
    const herdsWithData =
      newReportType === "cjto"
        ? (values.herds || []).map((h) => ({
            id: h.id,
            carcassHarnessingPercentage: h.carcassHarnessingPercentage,
          }))
        : null;

    setFieldValue("arrobaProductionContract", {
      ...currentContract,
      ...updates,
      reportType: newReportType,
      herdsWithData,
      uniqueData:
        newReportType === "unico" ? (updates.uniqueData ?? uniqueData) : null,
    });
  };

  const handleReportTypeChange = (e) => {
    if(e.target.value === 'unico') {
      const updatedHerds = (values.herds || []).map((h) =>
        ({ ...h, carcassHarnessingPercentage: null }),
    );
    setFieldValue("herds", updatedHerds);
    updateArrobaProductionContract({ reportType: e.target.value, herdsWithData: updatedHerds });
  } else {
      updateArrobaProductionContract({
        reportType: e.target.value,
        uniqueData: {
          carcassHarnessingPercentage: null,
        },
      });
    }
  };

  const handleHerdDataChange = (herdId, field, value) => {
    const updatedHerds = (values.herds || []).map((h) =>
      h.id === herdId ? { ...h, [field]: value } : h,
    );
    setFieldValue("herds", updatedHerds);

    // Atualiza herdsWithData no arrobaProductionContract se reportType for "cjto"
    if (reportType === "cjto") {
      const herdsWithData = updatedHerds.map((h) => ({
        id: h.id,
        carcassHarnessingPercentage: h.carcassHarnessingPercentage,
      }));
      updateArrobaProductionContract({ herdsWithData });
    }
  };

  const handleUniqueDataChange = (field, value) => {
    const updatedUniqueData = { ...uniqueData, [field]: value };
    updateArrobaProductionContract({ uniqueData: updatedUniqueData });
  };

  const handleValuePerArrobaChange = (value) => {
    updateArrobaProductionContract({ valuePerArroba: value });
  };

  const showCarcassHarnessingPercentage = useMemo(() => {
    return reportType === "cjto";
  }, [reportType]);

  const showUniqueRow = () => {
    return reportType === "unico";
  };

  return (
    <Card
      style={{
        marginTop: 16,
        borderRadius: 7,
        background: "#fff",
        boxShadow: "0px 0px 10px #d8d8d8",
      }}
    >
      <Row type="flex" gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Row>
            <label
              className={
                errors.arrobaProductionContract?.reportType && submitCount > 0
                  ? "error"
                  : ""
              }
            >
              {
                translation.boitelContractsPage.form.arrobaProductionContract
                  .report
              }
              *
            </label>
          </Row>
          <Row>
            <Radio.Group value={reportType} onChange={handleReportTypeChange}>
              <Row type="flex" gutter={8}>
                <Col>
                  <RadioButton value="cjto">
                    {
                      translation.boitelContractsPage.form
                        .arrobaProductionContract.byLot
                    }
                  </RadioButton>
                </Col>
                <Col>
                  <RadioButton value="unico">
                    {
                      translation.boitelContractsPage.form
                        .arrobaProductionContract.unique
                    }
                  </RadioButton>
                </Col>
              </Row>
            </Radio.Group>
          </Row>
        </Col>
        <Col span={8}>
          <Row type="flex" gutter={8}>
            <Col span={12}>
              <label style={{ fontWeight: "bold" }}>
                {
                  translation.boitelContractsPage.form.arrobaProductionContract
                    .cjt
                }
              </label>
            </Col>
            <Col span={12}>
              <label style={{ fontWeight: "bold" }}
                      className={(errors.arrobaProductionContract?.herdsWithData?.some(h => h.carcassHarnessingPercentage)
                      || errors.arrobaProductionContract?.uniqueData?.carcassHarnessingPercentage) && submitCount > 0 ? "error" : ""}>
                {
                  translation.boitelContractsPage.form.arrobaProductionContract
                    .carcassHarnessingPercentage
                }*
              </label>
            </Col>
          </Row>
          {(values.herds || []).map((herd, index) => (
            <Row
              key={herd.id}
              type="flex"
              gutter={8}
              style={{ marginBottom: 8 }}
            >
              <Col span={12}>
                <Input
                  value={index + 1}
                  disabled
                  style={{ textAlign: "center" }}
                />
              </Col>
              <Col span={12}>
                <NumberFormat
                  disabled={!showCarcassHarnessingPercentage}
                  customInput={Input}
                  value={herd.carcassHarnessingPercentage || ""}
                  placeholder={
                    translation.boitelContractsPage.form
                      .arrobaProductionContract
                      .carcassHarnessingPercentagePlaceholder
                  }
                  addonAfter="%"
                  decimalScale={2}
                  decimalSeparator=","
                  thousandSeparator="."
                  fixedDecimalScale={true}
                  onValueChange={({ floatValue }) =>
                    handleHerdDataChange(
                      herd.id,
                      "carcassHarnessingPercentage",
                      floatValue,
                    )
                  }
                  style={{ textAlign: "center" }}
                />
              </Col>
            </Row>
          ))}
          {showUniqueRow() && (
            <Row type="flex" gutter={8} style={{ marginTop: 8 }}>
              <Col span={12}></Col>
              <Col span={12}>
                <NumberFormat
                  style={{ textAlign: "center" }}
                  addonAfter="%"
                  customInput={Input}
                  value={uniqueData.carcassHarnessingPercentage || ""}
                  placeholder={
                    translation.boitelContractsPage.form
                      .arrobaProductionContract
                      .carcassHarnessingPercentagePlaceholder
                  }
                  decimalScale={2}
                  decimalSeparator=","
                  thousandSeparator="."
                  fixedDecimalScale={true}
                  onValueChange={({ floatValue }) =>
                    handleUniqueDataChange(
                      "carcassHarnessingPercentage",
                      floatValue,
                    )
                  }
                />
              </Col>
            </Row>
          )}
        </Col>
        <Col span={8}>
          <Row>
            <label
              className={
                errors.arrobaProductionContract?.valuePerArroba &&
                submitCount > 0
                  ? "error"
                  : ""
              }
            >
              {
                translation.boitelContractsPage.form.arrobaProductionContract
                  .valuePerArroba
              }
              *
            </label>
          </Row>
          <Row>
            <NumberFormat
              customInput={Input}
              value={valuePerArroba || ""}
              placeholder={
                translation.boitelContractsPage.form.arrobaProductionContract
                  .valuePerArrobaPlaceholder
              }
              addonBefore="R$"
              addonAfter="/@"
              decimalScale={2}
              decimalSeparator=","
              thousandSeparator="."
              fixedDecimalScale={true}
              onValueChange={({ floatValue }) =>
                handleValuePerArrobaChange(floatValue)
              }
            />
          </Row>
        </Col>
      </Row>
    </Card>
  );
};

export default ArrobaProductionCard;
